import { ARENA } from '../../config/constants.js';

const CONTACT_COOLDOWN_DURATION = 0.5;
const HIT_FLASH_DURATION = 0.08;

/**
 * Sinkweight (Act II, ACT2-BIBLE.md §8) — a Ballast echo: heavy, slow, and
 * everything nearby falls toward it.
 *
 * Two halves, split by who owns what. The tether on the *player* is applied
 * here (a weak inward drag whenever you're inside `well.radius`, so holding
 * your ground near one costs you). The bend on projectiles is applied by
 * Game._applyWells, which feeds this same `well` config into the shared
 * `applyGravityWell` the Ballast fight uses — one implementation, so a
 * Sinkweight curves your fire exactly the way the boss will.
 *
 * Slow and tanky on purpose: it isn't trying to reach you, it's trying to make
 * the ground around it a bad place to fight from.
 */
export class Sinkweight {
  constructor(eventBus, {
    x, y, radius = 8, hp, moveSpeed, contactDamage,
    wellStrength = 34, wellRadius = 78, playerPull = 26,
  }) {
    this.eventBus = eventBus;
    this.type = 'sinkweight';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    // Read by Game._applyWells and fed to gravityWell.js — the same shape the
    // boss's `bossDef.ballast` uses.
    this.well = { strength: wellStrength, radius: wellRadius };
    this.playerPull = playerPull; // px/sec of inward drag on the player
    this.isDead = false;
    this._contactCooldown = 0;
    this.hitFlashTimer = 0;
  }

  update(dt, player) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;
    this.x += (dx / dist) * this.moveSpeed * dt;
    this.y += (dy / dist) * this.moveSpeed * dt;
    this._clampToArena();

    // Drag the player inward, falling off with distance so the edge of the
    // well is a nudge and the middle is a real cost. Never past the center —
    // a hard pull would read as a shove out the far side.
    if (dist < this.well.radius) {
      const falloff = 1 - dist / this.well.radius;
      const step = Math.min(this.playerPull * falloff * dt, dist);
      player.x -= (dx / dist) * step;
      player.y -= (dy / dist) * step;
    }

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    if (this._contactCooldown <= 0 && dist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: hpBefore - this.hp, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
    }
  }
}
